import {
  type QueryClient,
  type QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import {
  isPayrollStatementDeliveryInProgress,
  isSmsDeliveryInFlight,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/delivery-status-rules";
import {
  DEFAULT_MAX_POLL_INTERVAL_MS,
  DEFAULT_POLL_TIMEOUT_MS,
  pollPayrollStatementStatus,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/polling";
import type {
  PayrollStatementDeliveryStatusResponse,
  PayrollStatementNotificationChannel,
  PayrollStatementNotificationSendResponse,
  PayrollStatementNotificationState,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import { mergePayrollStatementDeliveryStatuses } from "@/payroll/pay-breakdown/payroll-statement-notifications/utils";
import {
  payrollStatementDeliveryStatusKey,
  payrollStatementNotificationsKey,
  payrollStatementNotificationsPath,
} from "./keys";
import { payrollStatementNotificationsQueryOptions } from "./notifications";

export const useSendPayrollStatementNotifications = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      technicianIds,
      channels,
    }: {
      technicianIds: string[];
      channels?: PayrollStatementNotificationChannel[] | null;
    }) => {
      if (!businessId || !periodStart) {
        throw new Error("Pay period is required");
      }

      return api
        .post(
          `${payrollStatementNotificationsPath(businessId, periodStart)}/send`,
          {
            technician_ids: technicianIds,
            ...(channels ? { channels } : {}),
          },
        )
        .then((res) => res.data as PayrollStatementNotificationSendResponse);
    },
    onSuccess: (response) => {
      queryClient.setQueriesData<PayrollStatementNotificationState>(
        { queryKey: payrollStatementNotificationsKey(businessId, periodStart) },
        (state) =>
          mergePayrollStatementDeliveryStatuses(state, response.deliveries),
      );
    },
  });
};

export const payrollStatementDeliveryStatusQueryOptions = (
  api: AxiosInstance,
  queryClient: QueryClient,
  businessId: string | null,
  periodStart: string | null,
  seed: PayrollStatementNotificationSendResponse | null,
) => {
  const batchId = seed?.batch.id ?? null;
  let seeded = false;

  return {
    queryKey: payrollStatementDeliveryStatusKey(
      businessId,
      periodStart,
      batchId,
    ),
    queryFn: async ({ signal }: QueryFunctionContext) => {
      if (!businessId || !periodStart || !seed) {
        throw new Error("Delivery batch is required");
      }

      const initial: PayrollStatementDeliveryStatusResponse = {
        batch: seed.batch,
        deliveries: seed.deliveries,
        poll_after_ms: 2_000,
        max_poll_interval_ms: DEFAULT_MAX_POLL_INTERVAL_MS,
        poll_timeout_ms: DEFAULT_POLL_TIMEOUT_MS,
      };
      const merge = (response: PayrollStatementDeliveryStatusResponse) => {
        queryClient.setQueriesData<PayrollStatementNotificationState>(
          {
            queryKey: payrollStatementNotificationsKey(businessId, periodStart),
          },
          (state) =>
            mergePayrollStatementDeliveryStatuses(state, response.deliveries),
        );
      };
      if (!seeded) {
        seeded = true;
        merge(initial);
      }

      const result = await pollPayrollStatementStatus({
        initial,
        isPending: (response) =>
          response.deliveries.some(
            (delivery) =>
              isPayrollStatementDeliveryInProgress(delivery.status) ||
              isSmsDeliveryInFlight(delivery.sms),
          ),
        request: (requestSignal) =>
          api
            .get(
              `${payrollStatementNotificationsPath(
                businessId,
                periodStart,
              )}/delivery_status`,
              { params: { batch_id: batchId }, signal: requestSignal },
            )
            .then((res) => res.data as PayrollStatementDeliveryStatusResponse),
        signal,
        onUpdate: merge,
      });

      if (!result.polling_timed_out) {
        await queryClient.fetchQuery({
          ...payrollStatementNotificationsQueryOptions(
            api,
            businessId,
            periodStart,
          ),
          staleTime: 0,
        });
      }
      return result;
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
  };
};

export const usePayrollStatementDeliveryPolling = (
  businessId: string | null,
  periodStart: string | null,
  seed: PayrollStatementNotificationSendResponse | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useQuery({
    ...payrollStatementDeliveryStatusQueryOptions(
      api,
      queryClient,
      businessId,
      periodStart,
      seed,
    ),
    enabled: !!businessId && !!periodStart && !!seed,
  });
};
